import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { ThemeProvider } from "styled-components";
import styled from "styled-components";
import { Cookies } from "react-cookie-consent";
import { GlobalStyle } from "../style/global.js";
import Header from "./header.js";
import Footer from "./footer.js";
import USP from "./usp.js";
import IconLibrary from "./icon-library.js";
import PromoBanner from "./promobanner.js";
import CheckUser from "./check-user.js";
import SyncQuote from "./sync-quote.js";
import FaqPopup from "./pop-up.js";
import { checkCustomCSS, checkDarkMode } from "../utils/helpers.js";
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from "../context/global-context-provider.js";

const Wrapper = styled.main`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: 0 auto;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
`;

export const ConsentContext = React.createContext(null);

export default function Layout({ children, staticQueryData, location }) {
  const { isDarkMode } = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);

  useEffect(() => {
    // The signed-in user is only needed on the private /app/* pages (see CheckUser).
    if (window.location.pathname.startsWith("/app")) {
      dispatch({ type: "REQUIRE_USER" });
    }
  }, [dispatch]);

  const { wp } =
    staticQueryData ||
    useStaticQuery(graphql`
      query LayoutQuery {
        wp {
          crbThemeOptions {
            crbActivatePopup
            crbPaddingBottom
            crbPaddingLeft
            crbPaddingRight
            crbPaddingTop
            crbPromoEnabled
            crbPromoText
            crbUsp {
              icon {
                json
              }
              text
              url
            }
            crbUspAdditional {
              icon {
                json
              }
              text
              url
            }
            crbColorAccent1
            crbColorAccent2
            crbColorAccent3
            crbColorBlack
            crbColorPrimary
            crbColorSecondary
            crbColorWhite
            crbColorHeaderBackground
            crbColorHeaderLink
            crbColorHeaderButton
            crbColorHeaderButtonText
            crbColorHeaderSearch
            crbColorFooterBackgroundTop
            crbColorFooterBackgroundBottom
            crbColorFooterTextTop
            crbColorFooterTextBottom
            crbColorFooterButton
            crbColorFooterButtonText
            crbColorUspBackground
            crbColorUspBorderBottom
            crbColorUspIcon
            crbColorUspText
            crbColorPromobanner
            crbColorPromobannerLink
            crbColorPromobannerText
            crbSiteKey
          }
          customCSS
        }
      }
    `);

  const theme = useMemo(() => {
    const baseTheme = {
      name: "Default Theme from WP",
      colors: {
        primary: wp.crbThemeOptions.crbColorPrimary,
        secondary: checkDarkMode(wp.crbThemeOptions.crbColorSecondary, {
          isDarkMode: isDarkMode,
        }),
        black: isDarkMode ? "#f5f5f5" : wp.crbThemeOptions.crbColorBlack,
        white: isDarkMode ? "#2a2a2a" : wp.crbThemeOptions.crbColorWhite,
        accent1: checkDarkMode(wp.crbThemeOptions.crbColorAccent1, {
          isDarkMode: isDarkMode,
        }),
        accent2: checkDarkMode(wp.crbThemeOptions.crbColorAccent2, {
          isDarkMode: isDarkMode,
        }),
        accent3: checkDarkMode(wp.crbThemeOptions.crbColorAccent3, {
          isDarkMode: isDarkMode,
        }),
        darkGray: isDarkMode ? "#ddd" : "#333",
        lightGray: isDarkMode ? "#999" : "#6e6e6e",
        headerBackground: checkDarkMode(
          //wp.crbThemeOptions.crbColorHeaderBackground,
          "#ffffff",
          { isDarkMode: isDarkMode }
        ),
        headerLink: checkDarkMode(wp.crbThemeOptions.crbColorHeaderLink, {
          isDarkMode: isDarkMode,
        }),
        headerButton: wp.crbThemeOptions.crbColorHeaderButton,
        headerButtonText: checkDarkMode(
          wp.crbThemeOptions.crbColorHeaderButtonText,
          { isDarkMode: isDarkMode }
        ),
        headerSearch: checkDarkMode(wp.crbThemeOptions.crbColorHeaderSearch, {
          isDarkMode: isDarkMode,
        }),
        footerBackgroundTop: checkDarkMode(
          wp.crbThemeOptions.crbColorFooterBackgroundTop,
          { isDarkMode: isDarkMode }
        ),
        footerTextTop: checkDarkMode(wp.crbThemeOptions.crbColorFooterTextTop, {
          isDarkMode: isDarkMode,
        }),
        footerBackgroundBottom: checkDarkMode(
          wp.crbThemeOptions.crbColorFooterBackgroundBottom,
          { isDarkMode: isDarkMode }
        ),
        footerTextbottom: checkDarkMode(
          wp.crbThemeOptions.crbColorFooterTextBottom,
          { isDarkMode: isDarkMode }
        ),
        footerButton: checkDarkMode(wp.crbThemeOptions.crbColorFooterButton, {
          isDarkMode: isDarkMode,
        }),
        footerButtonText: checkDarkMode(
          wp.crbThemeOptions.crbColorFooterButtonText,
          { isDarkMode: isDarkMode }
        ),
        uspBackground: checkDarkMode(
          //wp.crbThemeOptions.crbColorUspBackground,
          "#ffffff",
          {
            isDarkMode: isDarkMode,
          }
        ),
        uspText: checkDarkMode(wp.crbThemeOptions.crbColorUspText, {
          isDarkMode: isDarkMode,
        }),
        uspBorderBottom: checkDarkMode(
          wp.crbThemeOptions.crbColorUspBorderBottom,
          { isDarkMode: isDarkMode }
        ),
        uspIcon: checkDarkMode(wp.crbThemeOptions.crbColorUspIcon, {
          isDarkMode: isDarkMode,
        }),
        promobannerText: checkDarkMode(
          wp.crbThemeOptions.crbColorPromobannerText,
          { isDarkMode: isDarkMode }
        ),
        promobannerBackground: checkDarkMode(
          wp.crbThemeOptions.crbColorPromobanner,
          { isDarkMode: isDarkMode }
        ),
        promobannerLink: checkDarkMode(
          wp.crbThemeOptions.crbColorPromobannerLink,
          { isDarkMode: isDarkMode }
        ),
      },
      fontHeadings: {
        fontFamily: "Poppins-Regular, Arial",
      },
      fontLinks: {
        fontFamily: "Poppins-Regular, Arial",
      },
      fontParagraphs: {
        fontFamily: "Poppins-Regular, Arial",
      },
      typography: {
        fontFamily: "Arial, sans-serif",
      },
      breakpoints: {
        wide: "1180px",
        wideTablet: "1025px",
        tablet: "850px",
        phone: "600px",
      },
      customCSS: checkCustomCSS(wp.customCSS, { isDarkMode: isDarkMode }),
      isDarkMode: isDarkMode,
    };

    return baseTheme;
  }, [wp.crbThemeOptions, wp.customCSS, isDarkMode]);

  const [consent, setConsent] = useState({
    googleMaps: undefined,
    googleTagManager: undefined,
  });

  const toKebabCase = (str) => str.replace(/[A-Z]/g, "-$&").toLowerCase();
  const toCamelCase = (str) => str.replace(/-./g, (x) => x[1].toUpperCase());

  const updateConsent = (values) => {
    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      const eventDetail = Object.keys(values).reduce((choices, key) => {
        choices[toKebabCase(key)] = values[key] ? "granted" : "denied";
        return choices;
      }, {});

      document.dispatchEvent(
        new CustomEvent("update-mev-consent", { detail: eventDetail })
      );
    } else {
      Object.keys(values).forEach((key) => {
        Cookies.set(`consent_${key}`, values[key].toString(), { expires: 365 });
      });
    }

    setConsent({ ...consent, ...values });
  };

  const handleMevConsent = (event) => {
    const newConsent = Object.keys(event.detail).reduce((choices, key) => {
      choices[toCamelCase(key)] = event.detail[key] === "granted";
      return choices;
    }, {});

    setConsent({ ...consent, ...newConsent });
  };

  useEffect(() => {
    const mevConsentListener = (event) => handleMevConsent(event);

    if (process.env.GATSBY_CONSENT_SCRIPT_URL) {
      document.addEventListener("mev-consent", mevConsentListener);
    }

    const consentCookieValues = Object.keys(consent).reduce((values, key) => {
      values[key] = Cookies.get(`consent_${key}`) === "true";
      return values;
    }, {});

    setConsent({ ...consent, ...consentCookieValues });

    return () => {
      document.removeEventListener("mev-consent", mevConsentListener);
    };
  }, []);

  return (
    <>
      <ConsentContext.Provider value={{ consent, updateConsent }}>
        <ThemeProvider theme={theme}>
          <IconLibrary />
          <Header
            staticQueryData={staticQueryData}
            location={location}
            staticSearchData={staticQueryData}
          >
            <PromoBanner
              promoEnabled={wp.crbThemeOptions.crbPromoEnabled}
              promoText={wp.crbThemeOptions.crbPromoText}
            />
            <USP
              list={wp?.crbThemeOptions?.crbUsp}
              additional={wp?.crbThemeOptions?.crbUspAdditional}
              paddingTop={wp?.crbThemeOptions?.crbPaddingTop}
              paddingBottom={wp?.crbThemeOptions?.crbPaddingBottom}
              paddingRight={wp?.crbThemeOptions?.crbPaddingRight}
              paddingLeft={wp?.crbThemeOptions?.crbPaddingLeft}
            />
          </Header>
          <GlobalStyle />
          <Wrapper>{children}</Wrapper>
          {wp?.crbThemeOptions?.crbActivatePopup && <FaqPopup />}
          <Footer staticQueryData={staticQueryData} />
          <CheckUser />
          <SyncQuote />
        </ThemeProvider>
      </ConsentContext.Provider>
    </>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  staticQueryData: PropTypes.object,
  location: PropTypes.object,
};

Layout.defaultProps = {
  children: undefined,
  staticQueryData: undefined,
  location: undefined,
};
